import { useEffect, useState } from 'react';
import type { AgentKind, AgentProfile, AutonomyLevel, ProfileDraft } from '@cm/protocol';
import { AUTONOMY_LABELS, emptyProfileDraft } from '@cm/protocol';
import { api } from '../api';
import { useStore } from '../store';

type Editing = { id: string | null; draft: ProfileDraft };

export function ProfilePanel() {
  const profiles = useStore((s) => s.profiles);
  const agents = useStore((s) => s.agents);
  const pushToast = useStore((s) => s.pushToast);
  const refreshProfiles = useStore((s) => s.refreshProfiles);
  const openPanel = useStore((s) => s.openPanel);

  const [editing, setEditing] = useState<Editing | null>(null);
  const [preview, setPreview] = useState<{ argv: string[]; warnings: string[] } | null>(null);
  const [busy, setBusy] = useState(false);

  // O preview do comando vem do daemon, que e quem monta o argv de verdade --
  // duplicar essa logica no cliente daria divergencia na primeira mudanca.
  useEffect(() => {
    if (!editing?.id) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    api
      .previewProfile(editing.id)
      .then((res) => {
        if (!cancelled) setPreview(res);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [editing?.id, profiles]);

  async function save() {
    if (!editing) return;
    setBusy(true);
    try {
      const res = editing.id
        ? await api.updateProfile(editing.id, editing.draft)
        : await api.createProfile(editing.draft);
      await refreshProfiles();
      setEditing({ id: res.profile.id, draft: toDraft(res.profile) });
      pushToast({ kind: 'success', text: 'perfil salvo' });
      for (const warning of res.warnings ?? []) pushToast({ kind: 'warn', text: warning });
    } catch (err) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  }

  async function remove(profile: AgentProfile) {
    if (!window.confirm(`Excluir o perfil "${profile.name}"?`)) return;
    try {
      await api.deleteProfile(profile.id);
      if (editing?.id === profile.id) setEditing(null);
      await refreshProfiles();
    } catch (err) {
      pushToast({ kind: 'error', text: err instanceof Error ? err.message : String(err) });
    }
  }

  if (editing) {
    const { draft } = editing;
    const patch = (fields: Partial<ProfileDraft>) =>
      setEditing({ ...editing, draft: { ...draft, ...fields } });
    const capability = agents.find((a) => a.agent === draft.agent);
    const levels = capability?.supportedAutonomy ?? [];

    return (
      <div className="panel">
        <div className="panel-head">
          <h2>{editing.id ? 'Editar perfil' : 'Novo perfil'}</h2>
          <button className="ghost" onClick={() => setEditing(null)}>
            ✕
          </button>
        </div>

        {preview && (
          <>
            <label>Comando que vai rodar</label>
            <div className="argv-preview">{preview.argv.join(' ')}</div>
            {preview.warnings.map((warning) => (
              <div className="warn-box" key={warning}>
                {warning}
              </div>
            ))}
          </>
        )}

        <div className="field">
          <label>Nome</label>
          <input value={draft.name} onChange={(e) => patch({ name: e.target.value })} />
        </div>

        <div className="two-col">
          <div className="field">
            <label>Agente</label>
            <select
              value={draft.agent}
              onChange={(e) => {
                const nextAgent = e.target.value as AgentKind;
                const nextLevels = agents.find((a) => a.agent === nextAgent)?.supportedAutonomy ?? [];
                patch({
                  agent: nextAgent,
                  autonomy: nextLevels.includes(draft.autonomy)
                    ? draft.autonomy
                    : (nextLevels[0] ?? 'manual'),
                });
              }}
            >
              {agents.map((a) => (
                <option key={a.agent} value={a.agent}>
                  {a.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Autonomia</label>
            <select
              value={draft.autonomy}
              onChange={(e) => patch({ autonomy: e.target.value as AutonomyLevel })}
            >
              {levels.map((level) => (
                <option key={level} value={level}>
                  {AUTONOMY_LABELS[level]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="field">
          <label>Diretório de trabalho</label>
          <input value={draft.cwd} onChange={(e) => patch({ cwd: e.target.value })} />
        </div>

        <div className="field">
          <label>Modelo</label>
          <input
            value={draft.model ?? ''}
            placeholder="padrão do CLI"
            onChange={(e) => patch({ model: e.target.value || null })}
          />
        </div>

        <div className="field">
          <label>Append system prompt</label>
          <textarea
            value={draft.appendSystemPrompt ?? ''}
            placeholder="Instruções de otimização anexadas ao system prompt."
            onChange={(e) => patch({ appendSystemPrompt: e.target.value || null })}
          />
        </div>

        <div className="field">
          <label>Ferramentas liberadas sem perguntar (uma por linha)</label>
          <textarea
            value={draft.allowedTools.join('\n')}
            placeholder={'Bash(git *)\nEdit'}
            onChange={(e) => patch({ allowedTools: splitLines(e.target.value) })}
          />
        </div>

        <div className="field">
          <label>Diretórios extras liberados (um por linha)</label>
          <textarea
            value={draft.addDirs.join('\n')}
            onChange={(e) => patch({ addDirs: splitLines(e.target.value) })}
          />
        </div>

        <div className="field">
          <label>Arquivo de config MCP</label>
          <input
            value={draft.mcpConfigPath ?? ''}
            placeholder="/caminho/para/.mcp.json"
            onChange={(e) => patch({ mcpConfigPath: e.target.value || null })}
          />
        </div>

        <div className="checkbox-row">
          <input
            id="strict-mcp"
            type="checkbox"
            checked={draft.strictMcp}
            onChange={(e) => patch({ strictMcp: e.target.checked })}
          />
          <label htmlFor="strict-mcp">Usar somente os MCPs deste arquivo</label>
        </div>

        <div className="checkbox-row">
          <input
            id="skip-trust"
            type="checkbox"
            checked={draft.skipTrustPrompt}
            onChange={(e) => patch({ skipTrustPrompt: e.target.checked })}
          />
          <label htmlFor="skip-trust">Confiar no diretório sem perguntar</label>
        </div>

        <div className="field">
          <label>Variáveis de ambiente (CHAVE=valor, uma por linha)</label>
          <textarea
            value={envToText(draft.env)}
            placeholder={draft.agent === 'grok' ? 'XAI_API_KEY=xai-...' : 'ANTHROPIC_API_KEY=sk-ant-...'}
            onChange={(e) => patch({ env: textToEnv(e.target.value) })}
          />
          <p className="card-desc" style={{ marginTop: 6 }}>
            Guardadas cifradas no daemon (AES-256-GCM) e injetadas só no processo da sessão.
            {draft.agent === 'grok' && ' Para usar uma chave da API, informe XAI_API_KEY.'}
          </p>
        </div>

        <div className="field">
          <label>Argumentos extras (um por linha)</label>
          <textarea
            value={draft.extraArgs.join('\n')}
            onChange={(e) => patch({ extraArgs: splitLines(e.target.value) })}
          />
        </div>

        <div className="row end">
          <button onClick={() => setEditing(null)}>Fechar</button>
          <button className="primary" onClick={() => void save()} disabled={busy || !draft.name}>
            Salvar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Perfis</h2>
        <button
          className="ghost"
          title="Novo perfil"
          onClick={() =>
            setEditing({
              id: null,
              draft: emptyProfileDraft(agents.find((a) => a.installed)?.agent ?? 'claude', ''),
            })
          }
        >
          +
        </button>
        <button className="ghost" onClick={() => openPanel(null)}>
          ✕
        </button>
      </div>

      {profiles.map((profile) => (
        <div className="card" key={profile.id}>
          <div className="card-title">{profile.name}</div>
          <div className="card-desc">
            <span className={`agent-tag ${profile.agent}`}>{profile.agent}</span>{' '}
            {AUTONOMY_LABELS[profile.autonomy]} · {profile.cwd}
          </div>
          <div className="row">
            <button onClick={() => setEditing({ id: profile.id, draft: toDraft(profile) })}>
              editar
            </button>
            <button className="ghost danger" onClick={() => void remove(profile)}>
              excluir
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function toDraft(profile: AgentProfile): ProfileDraft {
  const { id, createdAt, updatedAt, ...draft } = profile;
  return draft;
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function envToText(env: Record<string, string>): string {
  return Object.entries(env)
    .map(([key, value]) => `${key}=${value}`)
    .join('\n');
}

function textToEnv(text: string): Record<string, string> {
  const env: Record<string, string> = {};
  for (const line of splitLines(text)) {
    const index = line.indexOf('=');
    if (index <= 0) continue;
    env[line.slice(0, index).trim()] = line.slice(index + 1).trim();
  }
  return env;
}
